/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

class PredictiveSearch extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form.search-form')
    this.input = this.form.querySelector('input[type="search"]')
    this.results = this.form.querySelector('#predictive-search')
    this.status = this.form.querySelector('#predictive-search-status')

    this.emptyContainer = this.querySelector('.search-empty')
    this.cartUpsells = this.querySelector('.cart-upsells')
    this.searchController = null

    this.input.addEventListener('input', window.theme.debounce((event) => {
      this.getSearchResults()
    }, 300).bind(this))

    document.querySelector('#offcanvas-search')?.addEventListener('shown.bs.offcanvas', () => {
      this.input.focus()
    })
    this.accessibilityEventIsInit = false
    this.selectedIndex = -1
  }

  async getSearchResults () {
    const searchTerm = this.input.value.trim()

    if (searchTerm.length) {
      this.searchController?.abort()
      this.searchController = new AbortController()

      try {
        const response = await fetch(`${window.Shopify.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=${this.dataset.resourcesType}&resources[limit]=10&section_id=predictive-search`, {
          signal: this.searchController.signal
        })

        if (!response.ok) {
          throw new Error('Predictive search request failed')
        }

        const data = await response.text()
        const resultsSection = new DOMParser()
          .parseFromString(data, 'text/html')
          .querySelector('#shopify-section-predictive-search')

        if (!resultsSection) {
          throw new Error('Predictive search response was missing its results section')
        }

        this.results.innerHTML = resultsSection.innerHTML

        this.emptyContainer.setAttribute('hidden', 'hidden')
        this.cartUpsells?.setAttribute('hidden', 'hidden')
        this.input.setAttribute('aria-expanded', 'true')
        this.status.textContent = this.querySelector('#predictive-search-results')?.dataset.resultsText ?? ''

        this.results.querySelectorAll('.jdgm-prev-badge__stars').forEach(elem => {
          elem.setAttribute('tabindex', '-1')
        })
        this.handleAccessibility()
      } catch (error) {
        if (error.name === 'AbortError') return
        this.resetSearchState()
      }
    } else {
      this.searchController?.abort()
      this.resetSearchState()
    }
  }

  resetSearchState () {
    this.selectedIndex = -1
    this.emptyContainer.removeAttribute('hidden')
    this.cartUpsells?.removeAttribute('hidden')
    this.results.innerHTML = ''
    this.input.setAttribute('aria-expanded', 'false')
    this.input.removeAttribute('aria-activedescendant')
    this.status.textContent = ''
  }

  handleAccessibility () {
    this.selectedIndex = -1
    this.input.removeAttribute('aria-activedescendant')

    function listenKeyup (event) {
      const items = this.querySelectorAll('#predictive-search-results a, #predictive-search-results button')

      if (!items.length) return

      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        items.forEach(elem => {
          elem.closest('[role="option"]')?.setAttribute('aria-selected', 'false')
        })
      }

      switch (event.code) {
      case 'ArrowUp':
        if (this.selectedIndex <= 0 || this.selectedIndex >= items.length) {
          this.selectedIndex = items.length
        }
        this.selectedIndex--
        items.forEach((elem, index) => {
          if (index === this.selectedIndex) {
            elem.focus()
            const option = elem.closest('[role="option"]')
            option?.setAttribute('aria-selected', 'true')
            if (option?.id) this.input.setAttribute('aria-activedescendant', option.id)
          }
        })
        break
      case 'ArrowDown':
        if (this.selectedIndex >= items.length - 1) {
          this.selectedIndex = -1
        }
        this.selectedIndex++
        items.forEach((elem, index) => {
          if (index === this.selectedIndex) {
            elem.focus()
            const option = elem.closest('[role="option"]')
            option?.setAttribute('aria-selected', 'true')
            if (option?.id) this.input.setAttribute('aria-activedescendant', option.id)
          }
        })
        break
      case 'Enter':
        break
      }
    }

    if (!this.accessibilityEventIsInit) {
      this.addEventListener('keyup', listenKeyup)
      this.accessibilityEventIsInit = true
    }
  }
}

customElements.define('predictive-search', PredictiveSearch)
